import { Component, Host, h, State, Listen, Prop, Method, Element, Fragment } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { geti18n } from '../../../utils/utils';
import axios from '../../../utils/get-axios';

@Component({
  tag: 'lilu-navigation',
  styleUrl: 'lilu-navigation.scss',
  shadow: true,
})
export class LiluNavigation {
  @Prop() navigationUrlGet: string;
  @State() menuJSON;
  @State() isLoading = true;
  @State() isMobileViewport = false;

  @Element() el: HTMLElement;

  forceRefresh = false;
  imageToShow = '';
  maxColumnItems = 0;

  slideIn: string = '';

  componentWillLoad() {
    this.isMobileViewport = this.isMobile();
  }

  componentDidLoad() {
    const observeMobileBreakpoint = new ResizeObserver(_ => {
      this.isMobileViewport = this.isMobile();
    });

    observeMobileBreakpoint.observe(document.querySelector('lilu-header'));
  }

  isSelected(e) {
    if (e.menu?.visible) {
      return 'isSelected';
    } else {
      return '';
    }
  }

  hasMenu(e) {
    if (e.menu) {
      return 'hasMenu';
    } else {
      return '';
    }
  }

  componentDidUpdate() {
    if (this.slideIn) {
      const toSlideIn = this.slideIn;
      this.slideIn = '';
      this.el?.shadowRoot?.querySelector(`.${toSlideIn}`).classList.add('slide-in');

      setTimeout(() => {
        this.el?.shadowRoot?.querySelector('.slide-in')?.classList.remove('slide-in');
      }, 300);
    }
  }

  navigateToPage(e, page, openParent = false) {
    if (!this.isMobile()) {
      return;
    } // handle click on desktop as normal click

    e.preventDefault();
    if (!page.menu || openParent) {
      window.location.href = page.link;
    } else {
      this.slideIn = page.menu.level;
      const clonedMenu = this.cloneJSON();
      const clonedMenuItem = this.findElements(clonedMenu.items, page.uuid);
      clonedMenuItem.menu.visible = true;
      this.updateJSON(clonedMenu);
    }
  }

  navigateBack(parent) {
    const pageToBeRemoved = this.el?.shadowRoot?.querySelector(`.${parent.menu.level}`);
    pageToBeRemoved.classList.add('slide-out');

    pageToBeRemoved.addEventListener('animationend', () => {
      const clonedMenu = this.cloneJSON();
      const clonedMenuItem = this.findElements(clonedMenu.items, parent.uuid);
      clonedMenuItem.menu.visible = false;
      this.updateJSON(clonedMenu);
    });
  }

  switchLanguage(selectedLanguage: string) {
    const urlWithNewLanguage = location.href.replace(`/${document.querySelector('html').lang}`, selectedLanguage);
    location.href = urlWithNewLanguage;
  }

  connectedCallback() {
    this.isLoading = true;
    axios
      .get(this.navigationUrlGet, '')
      .then(response => {
        this.maxColumnItems = 0;
        this.menuJSON = response.data.menu;
        this.isLoading = false;
      })
      .catch(error => {
        console.error(error);
      });
  }

  /**
   * Called from outside to reset the visible state of a menu item and it's children
   */
  @Method()
  async resetNavigationState() {
    const menuJSONItems = this.resetVisible(this.menuJSON.items, '');
    const menuJSON = this.menuJSON;
    menuJSON.items = menuJSONItems;
    this.menuJSON = JSON.parse(JSON.stringify(menuJSON));
  }

  /**
   * Handle mouseover and switch to different menu item
   * Used on desktop only
   * @param e MouseOverEvent
   */
  @Listen('mouseoverLiluLink')
  handleMouseoverLiluLinkEvent(e) {
    if (this.isMobile()) {
      return;
    } // don't handle event when tablet / mobile

    const linkObj = this.getEventObject(e);

    if (linkObj.classList.contains('nav_link')) {
      const clonedMenu = this.cloneJSON();
      const clonedMenuItem = this.findElements(clonedMenu.items, linkObj.dataset.uuid);

      if (clonedMenuItem.menu && !clonedMenuItem.menu.visible) {
        clonedMenuItem.menu.visible = true;
        this.forceRefresh = true;
      }

      if (this.forceRefresh) {
        this.updateJSON(clonedMenu);
      }
    }
  }

  /**
   * Check if current viewport is mobile
   * @returns boolean
   */
  isMobile(): boolean {
    return window.innerWidth < 992;
  }

  getEventObject(e) {
    return e.composedPath()[0];
  }

  /**
   * Creates a deep copy of the menu items object so no references are saved
   * @returns copied navigation item object
   */
  cloneJSON() {
    return JSON.parse(JSON.stringify(this.menuJSON));
  }

  /**
   * Updates the new menu items object. Triggers a state chnage and new navigation menu will be rendered
   * @param clonedMenu
   */
  updateJSON(clonedMenu: Array<any>) {
    this.menuJSON = clonedMenu;
    this.forceRefresh = false;
  }

  /**
   * Recursivly goes over all menu items and submenu's to find the max item count, which will be used to set the fitting navigation height
   * Used on desktop only
   * @param items
   */
  calculateNavigationHeight(items: Array<any>) {
    this.maxColumnItems = Math.max(this.maxColumnItems, items.length);
    items.forEach(e => {
      if (e.menu) {
        this.calculateNavigationHeight(e.menu.items);
      }
    });
  }

  /**
   * Resets all visible state for non matching uuids in navigation items array
   * @param items
   * @param id uuid
   * @returns items
   */
  resetVisible(items: Array<any>, id: string) {
    items.forEach(e => {
      if (e.menu && e.uuid !== id) {
        if (e.menu.visible) {
          this.forceRefresh = true;
          e.menu.visible = false;
        }
        this.resetVisible(e.menu.items, null);
      }
    });
    return items;
  }

  /**
   * Searches a menu item by uuid
   * @param items navigation items
   * @param id uuid of menu item to be found
   * @returns found menu item
   */
  findElements(items: Array<any>, id: string) {
    let element;

    items.forEach(e => {
      if (e.uuid === id) {
        element = e;
        this.resetVisible(items, id);
      } else if (e.menu && !element) {
        const resulttemp = this.findElements(e.menu.items, id);
        if (resulttemp !== null) {
          element = resulttemp;
        }
      }
    });
    return element;
  }

  /**
   * Renders the whole desktop navigation
   * @param items
   * @returns HtmlElements
   */
  renderDesktopNavigation(items: Array<any>) {
    if (this.maxColumnItems === 0) {
      // do only the first time
      this.calculateNavigationHeight(items);
      document.documentElement.style.setProperty('--navigation-max-rows', this.maxColumnItems.toString());
    }

    return this.addElements(items, 'first');
  }

  /**
   * Renders the whole mobile navigation
   * @param items
   * @returns HtmlElements
   */
  renderMobileNavigation(items: Array<any>) {
    return this.renderMobilePage(items, 'first');
  }

  /**
   * Renders each mobile navigation page in a recusrive manner
   * @param items
   * @param level
   * @param parent
   * @param grandParent
   * @returns HtmlElements
   */
  renderMobilePage(items: Array<any>, level, parent = null, grandParent = null) {
    let nextPage = null;
    const menuItems = [];

    items.forEach(e => {
      e.uuid = uuidv4();
      if (e.menu?.visible) {
        nextPage = e;
      }
      menuItems.push(this.createListElement(e, level));
    });

    return (
      <Fragment>
        <div class={`nav-page ${level}`}>
          {level !== 'first' && (
            <Fragment>
              <span class="back" onClick={() => this.navigateBack(parent)}>
                <lilu-icon size="size-14" color="nero" type="icon-chevron-left"></lilu-icon>
                {grandParent?.label ? grandParent.label : geti18n('navigation.backToLevel1')}
              </span>

              <h2
                onClick={e => {
                  e.preventDefault();
                }}
              >
                {parent.label}
              </h2>
            </Fragment>
          )}

          <ul class="menu first">
            {parent && parent.link && (
              <li class="item" data-uuid={parent.uuid} onClick={event => this.navigateToPage(event, parent, true)}>
                <lilu-link appearance={`nav_link`} data-uuid={parent.uuid} link={parent.link}>
                  {geti18n('navigation.discoverAll')}
                </lilu-link>
              </li>
            )}

            {menuItems}
          </ul>
        </div>
        {nextPage !== null && this.renderMobilePage(nextPage.menu.items, nextPage.menu.level, nextPage, parent)}
      </Fragment>
    );
  }

  /**
   * Recursivly renders th ul, li structure of visible menu items (Desktop only)
   * ul
   *   li
   *   li
   *   ul
   *     li
   *     li
   *   /ul
   *   li
   * /ul
   * @param items
   * @param level first | second | third
   * @returns HtmlElements
   */
  addElements(items: Array<any>, level: string) {
    const navItems = [];
    items.forEach(e => {
      e.uuid = uuidv4();
      if (!e.viewport || (e.viewport === 'mobile' && this.isMobile())) {
        navItems.push(this.createListElement(e, level));

        if (e.menu?.visible) {
          navItems.push(this.addElements(e.menu.items, e.menu.level));
        }
      }
    });

    return (
      <ul class={`menu ${level}`} data-level={level}>
        {navItems}
      </ul>
    );
  }

  /**
   * Renders one li Element
   * - Icon if has submenu
   * - Image if exists and viewport is desktop
   * @param e
   * @param level
   * @returns
   */
  createListElement(e, level: string) {
    return (
      <li class={`item ${this.hasMenu(e)} ${this.isSelected(e)} ${e.classes}`} data-uuid={e.uuid} onClick={event => this.navigateToPage(event, e)}>
        <lilu-link appearance={`nav_link ${this.isSelected(e)}`} data-uuid={e.uuid} class={`nav_link l_${level}`} link={e.link} colorcode={e.color} icon={e.icon}>
          {e.label}
        </lilu-link>

        {this.hasMenu(e) && <lilu-icon data-uuid={e.uuid} class="nav_link" size="size-14 lg-size-20" color="nero" type="icon-chevron-right"></lilu-icon>}

        {e.img && !this.isMobile() && this.createImgElement(e, level)}
      </li>
    );
  }

  /**
   * Renders an image for the navigation, should be used on desktop only
   * @param e
   * @param level
   * @returns HtmlElements
   */
  createImgElement(e, level: string) {
    return (
      <div data-uuid={e.uuid} class="img_container">
        <lilu-img class={`nav_img nav_img_${level}`} src={e.img} alt={`Image for category ${e.label}`}></lilu-img>
      </div>
    );
  }

  render() {
    if (this.isLoading) {
      return <Host>Loading...</Host>;
    } else {
      return (
        <Host>
          <nav>{this.isMobileViewport ? this.renderMobileNavigation(this.menuJSON.items) : this.renderDesktopNavigation(this.menuJSON.items)}</nav>
        </Host>
      );
    }
  }
}
